export const RUNTIME_NAVIGATION_MESSAGE = "applyt:runtime-navigation";
export const RUNTIME_NAVIGATE_MESSAGE = "applyt:runtime-navigate";

export type RuntimeRoute = { pathname: string; search: string; hash: string; state?: unknown; key?: string };
export type RuntimeNavigationSnapshot = { entries: RuntimeRoute[]; index: number; historyKey?: string; documentRoute?: string };

const STORAGE_PREFIX = "applyt:runtime-navigation:v1:";

// Navigation state is data from an untrusted, opaque-origin frame. It never
// supplies a proxy origin, ticket, App identity, or permission to the host.
export function runtimeInternalRoute(value: string, proxyBaseUrl: string, current = "/"): string | null {
  // `srcdoc` is the pathname of the iframe's about:srcdoc document, not an
  // application route. Older bootstraps persisted it as a document destination.
  const reservedPath = /^\/(?:srcdoc|api|auth|oauth|functions|applications|operating-systems|assets|src|node_modules|@[^/]*)(?:\/|$)/i;
  if (typeof value !== "string" || !value.trim() || value.length > 4096) return null;
  const raw = value.trim();
  if (/^[\\/]{2}|\\/.test(raw) || Array.from(raw).some((character) => character.charCodeAt(0) < 32)) return null;
  try {
    const proxy = new URL(proxyBaseUrl);
    const url = new URL(raw, `${proxy.origin}${current}`);
    if (url.origin !== proxy.origin || url.username || url.password) return null;
    let path = url.pathname;
    if (path.startsWith(`${proxy.pathname}/`)) path = path.slice(proxy.pathname.length);
    // Reject traversal before URL normalization, including nested escaping.
    let decoded = raw.split(/[?#]/, 1)[0];
    for (let i = 0; i < 4; i += 1) {
      if (/(?:^|\/)\.\.(?:\/|$)|\\|%(?:2f|5c)/i.test(decoded)) return null;
      const next = decodeURIComponent(decoded);
      if (next === decoded) break;
      decoded = next;
    }
    let decodedPath = path;
    for (let i = 0; i < 4; i += 1) {
      const next = decodeURIComponent(decodedPath);
      if (next === decodedPath) break;
      decodedPath = next;
    }
    if (/%|\\/.test(decoded) || reservedPath.test(decodedPath) || /\.[a-z0-9]{1,8}$/i.test(decodedPath)) return null;
    if ([...url.searchParams.keys()].some((key) => /^(?:_preview_token|(?:access|refresh|id)_token|token|proxy_ticket|__applyt_ticket|secret|authorization)$/i.test(key))) return null;
    return `${path}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function sanitizeRuntimeNavigation(value: unknown): RuntimeNavigationSnapshot | null {
  try {
    if (JSON.stringify(value).length > 64 * 1024) return null;
    const candidate = value as RuntimeNavigationSnapshot;
    if (!Array.isArray(candidate?.entries) || !candidate.entries.length || candidate.entries.length > 100
      || !Number.isInteger(candidate.index) || candidate.index < 0 || candidate.index >= candidate.entries.length) return null;
    const entries: RuntimeRoute[] = [];
    for (const route of candidate.entries) {
      if (typeof route?.pathname !== "string" || typeof route.search !== "string" || typeof route.hash !== "string") return null;
      const path = `${route.pathname}${route.search}${route.hash}`;
      if (!route.pathname.startsWith("/") || runtimeInternalRoute(path, "https://runtime.invalid/proxy") !== path) return null;
      const parsed = new URL(path, "https://runtime.invalid");
      if (parsed.pathname !== route.pathname || parsed.search !== route.search || parsed.hash !== route.hash) return null;
      if (route.key !== undefined && (typeof route.key !== "string" || route.key.length > 128)) return null;
      entries.push({ pathname: route.pathname, search: route.search, hash: route.hash, state: route.state, key: route.key });
    }
    const historyKey = typeof candidate.historyKey === "string" && /^[a-z0-9]{1,32}$/.test(candidate.historyKey) ? candidate.historyKey : undefined;
    const current = entries[candidate.index];
    const documentRoute = candidate.documentRoute;
    if (documentRoute !== undefined && documentRoute !== `${current.pathname}${current.search}${current.hash}`) return null;
    return { entries, index: candidate.index, historyKey, documentRoute };
  } catch {
    return null;
  }
}

export function readRuntimeNavigation(appId: string): RuntimeNavigationSnapshot | null {
  try {
    const key = `${STORAGE_PREFIX}${appId}`;
    const stored = sessionStorage.getItem(key);
    const snapshot = sanitizeRuntimeNavigation(JSON.parse(stored || "null"));
    if (stored && !snapshot) sessionStorage.removeItem(key);
    return snapshot;
  }
  catch { return null; }
}

export function persistRuntimeNavigation(appId: string, value: unknown): boolean {
  const snapshot = sanitizeRuntimeNavigation(value);
  if (!appId || !snapshot) return false;
  try { sessionStorage.setItem(`${STORAGE_PREFIX}${appId}`, JSON.stringify(snapshot)); return true; }
  catch { return false; }
}

function scriptJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/&/g, "\\u0026").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

export function injectRuntimeNavigationSeed(html: string, snapshot: RuntimeNavigationSnapshot | null): string {
  const script = `<script data-applyt-navigation-seed="true">window.__APPLYT_RUNTIME_NAVIGATION_SEED__=${scriptJson(sanitizeRuntimeNavigation(snapshot))};</script>`;
  const existing = /<script\b[^>]*data-applyt-navigation-seed="true"[^>]*>[\s\S]*?<\/script>/i;
  if (existing.test(html)) return html.replace(existing, () => script);
  if (/<head\b[^>]*>/i.test(html)) return html.replace(/<head\b[^>]*>/i, (head) => `${head}${script}`);
  return `${script}${html}`;
}

export function runtimeNavigationScript(proxyBaseUrl: string): string {
  return `<script data-applyt-runtime-navigation="true">
(function(){
  if(window.__APPLYT_RUNTIME_NAVIGATION__)return;
  var proxyBase=${scriptJson(proxyBaseUrl)};
  var resolve=${runtimeInternalRoute.toString()};
  var NativeURL=window.URL;
  var nativePush=history.pushState.bind(history),nativeReplace=history.replaceState.bind(history),nativeGo=history.go.bind(history);
  var saved=window.__APPLYT_RUNTIME_NAVIGATION_SEED__;
  delete window.__APPLYT_RUNTIME_NAVIGATION_SEED__;
  var entries=saved&&saved.entries||[{pathname:"/",search:"",hash:"",state:null,key:"default"}];
  var index=saved?saved.index:0;
  var listeners=[],driver=null,popTarget=null,action="POP",legacyReset=true,configured=!!saved;
  var frameKey=saved&&saved.historyKey||Math.random().toString(36).slice(2);
  function path(route){return route.pathname+route.search+route.hash;}
  function snapshot(){return{entries:entries.slice(),index:index,historyKey:frameKey,documentRoute:!listeners.length&&!driver?path(entries[index]):undefined};}
  function current(){return{location:entries[index],action:action};}
  function post(type,extra){try{window.parent.postMessage(Object.assign({type:type},extra),"*");}catch(_error){}}
  function publish(){
    listeners.slice().forEach(function(listener){listener(current());});
    post("${RUNTIME_NAVIGATION_MESSAGE}",{snapshot:snapshot()});
  }
  function mark(replace){
    var marker={__applytRoute:{frame:frameKey,key:entries[index].key},usr:entries[index].state,key:entries[index].key,idx:index};
    try{(replace?nativeReplace:nativePush)(marker,"");}catch(_error){}
  }
  function route(to,state){
    var value=typeof to==="string"?to:(to.pathname||entries[index].pathname)+(to.search||"")+(to.hash||"");
    var safe=resolve(value,proxyBase,path(entries[index]));
    if(!safe)return null;
    var url=new NativeURL(safe,new NativeURL(proxyBase).origin);
    return{pathname:url.pathname,search:url.search,hash:url.hash,state:state===undefined?null:state,key:Math.random().toString(36).slice(2)};
  }
  function commit(to,replace,state,fromDriver){
    var next=route(to,state);if(!next)return false;
    if(popTarget!==null){index=popTarget;popTarget=null;entries[index]=next;action="POP";mark(true);}
    else if(replace){entries[index]=next;action="REPLACE";mark(true);}
    else{entries=entries.slice(0,index+1);entries.push(next);index++;action="PUSH";mark(false);}
    if(entries.length>100){entries.shift();index--;mark(true);}
    publish();
    try{if(next.hash)setTimeout(function(){var target=document.getElementById(decodeURIComponent(next.hash.slice(1)));if(target)target.scrollIntoView();},0);}catch(_error){}
    if(!fromDriver&&!listeners.length)post("${RUNTIME_NAVIGATE_MESSAGE}",{route:path(next)});
    return true;
  }
  function navigate(to,options){
    options=options||{};
    if(typeof to==="number")return go(to);
    var next=route(to,options.state);if(!next)return false;
    if(driver){driver(path(next),options);return true;}
    return commit(next,!!options.replace,next.state,false);
  }
  function go(delta){
    if(!Number.isInteger(delta)||!delta)return;
    var next=index+delta;if(next<0||next>=entries.length)return;
    nativeGo(delta);
  }
  var api={current:current,snapshot:snapshot,navigate:navigate,
    initialize:function(options){
      if(configured)return;configured=true;options=options||{};
      var initial=options.initialEntries||[options.basename||"/"];
      var parsed=initial.map(function(entry){return route(entry,typeof entry==="object"?entry.state:null);}).filter(Boolean);
      if(parsed.length){entries=parsed;index=Number.isInteger(options.initialIndex)?Math.max(0,Math.min(options.initialIndex,entries.length-1)):entries.length-1;mark(true);}
      publish();
    },
    subscribe:function(listener){listeners.push(listener);return function(){listeners=listeners.filter(function(item){return item!==listener;});};},
    attach:function(nextDriver){driver=nextDriver;return function(){if(driver===nextDriver)driver=null;};},
    commit:function(location,type){
      if(path(location)===path(entries[index])&&location.key===entries[index].key)return;
      commit(location,type!=="PUSH",location.state,true);
    },
    navigator:{createHref:function(to){return typeof to==="string"?to:(to.pathname||"")+(to.search||"")+(to.hash||"");},
      encodeLocation:function(to){var next=route(to);return next||entries[index];},
      createURL:function(to){return new NativeURL(typeof to==="string"?to:path(to),new NativeURL(proxyBase).origin+path(entries[index]));},
      go:go,push:function(to,state){commit(to,false,state,false);},replace:function(to,state){commit(to,true,state,false);}}
  };
  window.__APPLYT_RUNTIME_NAVIGATION__=api;
  function hardNavigate(value,replace,property){
    var next=resolve(String(value),proxyBase,path(entries[index]));
    if(next){navigate(next,{replace:!!replace});return;}
    if(property)window.location[property]=value;
    else if(replace)window.location.replace(value);
    else window.location.assign(value);
  }
  api.assign=function(value){hardNavigate(value,false);};
  api.replace=function(value){hardNavigate(value,true);};
  Object.defineProperty(api,"location",{get:function(){return api;},set:function(value){hardNavigate(value,false);}});
  ["href","pathname","search","hash"].forEach(function(property){
    Object.defineProperty(api,property,{get:function(){return property==="href"?new NativeURL(path(entries[index]),new NativeURL(proxyBase).origin).href:entries[index][property];},set:function(value){
      if(property==="href"){hardNavigate(value,false,"href");return;}
      var url=new NativeURL(path(entries[index]),new NativeURL(proxyBase).origin);url[property]=value;
      var next=resolve(url.href,proxyBase,path(entries[index]));
      if(next)navigate(next);else window.location[property]=value;
    }});
  });
  function wrap(name,replace){
    var wrapped=function(state,unused,url){
      if(url===undefined||url===null||url===""){
        if(legacyReset&&state===null){legacyReset=false;return;}
        legacyReset=false;entries[index].state=state;mark(true);return;
      }
      // Generated startup code removes URL credentials with replaceState using
      // the native iframe pathname (srcdoc). Keep the virtual App location;
      // this updates history state without requesting another document.
      var nativeLocation=window.location;
      var targetPath=String(url).split(/[?#]/,1)[0];
      if(replace&&nativeLocation&&nativeLocation.protocol==="about:"&&nativeLocation.pathname==="srcdoc"
        &&(targetPath==="srcdoc"||targetPath==="about:srcdoc")){
        legacyReset=false;entries[index].state=state;mark(true);publish();return;
      }
      navigate(String(url),{replace:replace,state:state});
    };
    Object.defineProperty(wrapped,"__applytWrapped",{value:true});
    Object.defineProperty(wrapped,"__applytRuntimeProxyWrapped",{value:true});
    try{Object.defineProperty(history,name,{configurable:true,value:wrapped});}catch(_error){}
    try{Object.defineProperty(History.prototype,name,{configurable:true,value:wrapped});}catch(_error){}
  }
  wrap("pushState",false);wrap("replaceState",true);
  try{
    Object.defineProperty(history,"state",{configurable:true,get:function(){return entries[index].state;}});
    Object.defineProperty(history,"go",{configurable:true,value:go});
    Object.defineProperty(history,"back",{configurable:true,value:function(){go(-1);}});
    Object.defineProperty(history,"forward",{configurable:true,value:function(){go(1);}});
  }catch(_error){}
  mark(true);
  window.addEventListener("popstate",function(event){
    var marker=event.state&&event.state.__applytRoute;
    if(!marker||marker.frame!==frameKey)return;
    var targetIndex=entries.findIndex(function(entry){return entry.key===marker.key;});
    if(targetIndex<0)return;
    action="POP";
    if(driver){popTarget=targetIndex;driver(path(entries[targetIndex]),{replace:true,state:entries[targetIndex].state});}
    else{index=targetIndex;publish();if(!listeners.length)post("${RUNTIME_NAVIGATE_MESSAGE}",{route:path(entries[index])});}
  });
  document.addEventListener("click",function(event){
    if(event.defaultPrevented||event.button!==0)return;
    var anchor=event.target&&event.target.closest&&event.target.closest("a[href]");
    if(!anchor||anchor.hasAttribute("download"))return;
    var href=anchor.getAttribute("href");
    if(!href)return;
    var next=resolve(href,proxyBase,path(entries[index]));
    if(!next)return;
    // React's root handler has already had the first opportunity to handle the
    // click. Only native internal navigation reaches this document listener.
    event.preventDefault();navigate(next);
  });
  // Location.assign/replace and location.href are unforgeable browser APIs.
  // Cancel their internal destination at the navigation boundary, then ask the
  // same App router to render it. External navigation is left to the browser.
  if(window.navigation&&window.navigation.addEventListener){
    window.navigation.addEventListener("navigate",function(event){
      if(!event.cancelable||event.downloadRequest!=null||event.formData)return;
      var next=resolve(event.destination.url,proxyBase,path(entries[index]));
      if(!next)return;
      event.preventDefault();navigate(next,{replace:event.navigationType==="replace"});
    });
  }
})();
</script>`;
}

export { runtimeRouterModule } from "../../supabase/functions/_shared/osRuntimeNavigation";
